import React from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, ArrowLeft } from 'lucide-react';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg text-white leading-tight">Trac Jobs Alert</span>
                <span className="text-xs text-emerald-400">For Doctors</span>
              </div>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-sm font-medium text-white mb-4">Terms of Service</h1>
        
        <div className="prose prose-invert prose-slate prose-sm max-w-none space-y-4">
          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">1. Agreement to Terms</p>
            <p className="text-slate-400 text-xs">
              By accessing or using Trac Jobs Alert ("Service"), operated by Trac Jobs Alert Ltd ("we", "our", "us"), 
              you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these 
              terms, you may not access the Service.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">2. Description of Service</p>
            <p className="text-slate-400 text-xs">
              Trac Jobs Alert is a subscription-based job alert service that:
            </p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Monitors healthjobsuk.com for medical and dental job postings</li>
              <li>Sends email notifications when jobs matching your preferences are posted</li>
              <li>Provides direct links to job listings on third-party websites</li>
              <li>Allows you to manage job search preferences and alerts</li>
            </ul>
            <p className="text-slate-400 bg-slate-800 p-2 rounded border border-slate-700">
              <strong className="text-emerald-400">Important:</strong> We are a job alert aggregation service. 
              We do not employ, recruit, or make hiring decisions. All job applications are made directly 
              with the employers through their respective websites.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">3. Eligibility</p>
            <p className="text-slate-400 text-xs">To use our Service, you must:</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Be at least 18 years of age</li>
              <li>Be a medical or dental professional, or seeking employment in the healthcare sector</li>
              <li>Provide accurate and complete registration information</li>
              <li>Have the legal capacity to enter into a binding agreement</li>
            </ul>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">4. Account Registration</p>
            <p className="text-slate-400 text-xs">When creating an account, you agree to:</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorised access</li>
            </ul>
            <p className="text-slate-400 text-xs">
              We reserve the right to suspend or terminate accounts that contain false or misleading information.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">5. Subscription and Payment</p>
            
            <p className="text-xs text-slate-400">5.1 Subscription Plans</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Our Service is offered on a monthly subscription basis at £9.99/month</li>
              <li>Subscription includes unlimited email alerts and up to 5 saved job searches</li>
              <li>Prices may change with 30 days' notice</li>
            </ul>

            <p className="text-xs text-slate-400">5.2 Billing</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Subscriptions are billed monthly in advance</li>
              <li>Payment is processed securely through Stripe</li>
              <li>Your subscription will automatically renew unless cancelled</li>
            </ul>

            <p className="text-xs text-slate-400">5.3 Cancellation</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>You may cancel your subscription at any time</li>
              <li>Access continues until the end of your current billing period</li>
              <li>No refunds for partial months or unused service</li>
            </ul>

            <p className="text-xs text-slate-400">5.4 Discount Codes</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Discount codes are subject to specific terms and expiry dates</li>
              <li>Codes may be single-use or limited in number of uses</li>
              <li>We reserve the right to modify or discontinue promotions</li>
            </ul>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">6. Acceptable Use</p>
            <p className="text-slate-400 text-xs">You agree NOT to:</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Use the Service for any unlawful purpose</li>
              <li>Share your account credentials with others</li>
              <li>Attempt to gain unauthorised access to our systems</li>
              <li>Use automated systems to access the Service (bots, scrapers)</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Reproduce, duplicate, or resell the Service without permission</li>
              <li>Use the Service to send spam or unsolicited communications</li>
              <li>Misrepresent your identity or professional qualifications</li>
            </ul>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">7. Intellectual Property</p>
            <p className="text-slate-400 text-xs">
              The Service and its original content, features, and functionality are owned by 
              Trac Jobs Alert and are protected by international copyright, trademark, and other 
              intellectual property laws.
            </p>
            <p className="text-slate-400 text-xs">
              Job listing content is sourced from third-party websites and remains the property 
              of those respective sources.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">8. Third-Party Content and Links</p>
            <p className="text-slate-400 text-xs">
              Our Service provides links to third-party websites (including healthjobsuk.com and 
              employer websites). We are not responsible for:
            </p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>The accuracy or availability of third-party job listings</li>
              <li>The content, privacy policies, or practices of third-party sites</li>
              <li>Any loss or damage arising from your use of third-party services</li>
              <li>Employment decisions made by third-party employers</li>
            </ul>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">9. Communications</p>
            <p className="text-slate-400 text-xs">By using our Service, you consent to receive:</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li><strong className="text-white">Transactional emails:</strong> Job alerts, account notifications, payment confirmations</li>
              <li><strong className="text-white">Service updates:</strong> New features, changes to service, maintenance notices</li>
            </ul>
            <p className="text-slate-400 text-xs">
              You can manage your communication preferences in your account settings or by using 
              the unsubscribe link in our emails.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">10. Disclaimer of Warranties</p>
            <p className="text-slate-400 text-xs">
              The service is provided "as is" and "as available" without warranties of any kind, 
              either express or implied, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-slate-400 space-y-1 text-xs">
              <li>Warranties of merchantability or fitness for a particular purpose</li>
              <li>Accuracy, reliability, or completeness of job listing information</li>
              <li>Uninterrupted or error-free service</li>
              <li>Results from using the Service, including employment outcomes</li>
            </ul>
            <p className="text-slate-400 text-xs">
              Job listing disclaimer: We aggregate job listings from third-party sources. We do not guarantee the accuracy, 
              availability, or legitimacy of any job posting. Jobs may be filled, removed, or modified without notice. 
              We accept no responsibility for the content of external job listings or any decisions you make based on this information.
            </p>
            <p className="text-slate-400 text-xs">
              Notification disclaimer: We do not guarantee the delivery, timing, or completeness of job alert notifications. 
              Email notifications may be delayed, filtered by spam systems, or fail to deliver due to technical issues beyond our control. 
              Users should not rely solely on our notifications for time-sensitive job opportunities and are advised to regularly 
              check job boards directly.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">12. Limitation of Liability</p>
            <p className="text-slate-400 text-xs">
              Trac Jobs Alert accepts no liability whatsoever for any outcome resulting from your use of this service, 
              including but not limited to employment decisions, job applications, missed opportunities, or any other 
              consequences arising from the use of our job alert service.
            </p>
            <p className="text-slate-400 text-xs">
              To the maximum extent permitted by law, Trac Jobs Alert shall not be liable for:
            </p>
            <ul className="list-disc list-inside text-slate-400 space-y-1 text-xs">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, data, use, goodwill, or other intangible losses</li>
              <li>Damages resulting from your use or inability to use the Service</li>
              <li>Any employment decisions, outcomes, or missed job opportunities</li>
              <li>Actions of third-party employers or websites</li>
              <li>Inaccurate, incomplete, or outdated job listing information</li>
              <li>Any reliance you place on information provided through our Service</li>
            </ul>
            <p className="text-slate-400 text-xs">
              You use this Service entirely at your own risk. Our total liability shall not exceed 
              the amount you paid for the Service in the 12 months prior to the claim.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">13. Indemnification</p>
            <p className="text-slate-400 text-xs">
              You agree to indemnify and hold harmless Trac Jobs Alert, its affiliates, officers, 
              directors, employees, and agents from any claims, damages, losses, liabilities, and 
              expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Any content you submit or share through the Service</li>
            </ul>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">14. Service Modifications</p>
            <p className="text-slate-400 text-xs">
              We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) 
              at any time, with or without notice. We shall not be liable to you or any third party for 
              any modification, suspension, or discontinuation of the Service.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">15. Termination</p>
            <p className="text-slate-400 text-xs">
              We may terminate or suspend your account and access to the Service immediately, without 
              prior notice, for any reason, including:
            </p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Breach of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Non-payment of subscription fees</li>
              <li>At our sole discretion</li>
            </ul>
            <p className="text-slate-400 text-xs">
              Upon termination, your right to use the Service will cease immediately.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">16. Governing Law</p>
            <p className="text-slate-400 text-xs">
              These Terms shall be governed by and construed in accordance with the laws of England 
              and Wales, without regard to conflict of law provisions. Any disputes arising from these 
              Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">17. Changes to Terms</p>
            <p className="text-slate-400 text-xs">
              We reserve the right to modify these Terms at any time. We will provide notice of 
              significant changes by posting the updated Terms on this page and updating the 
              "Last updated" date. Your continued use of the Service after any changes constitutes 
              acceptance of the new Terms.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">18. Severability</p>
            <p className="text-slate-400 text-xs">
              If any provision of these Terms is found to be unenforceable or invalid, that provision 
              shall be limited or eliminated to the minimum extent necessary, and the remaining 
              provisions shall remain in full force and effect.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">19. Entire Agreement</p>
            <p className="text-slate-400 text-xs">
              These Terms, together with our Privacy Policy, constitute the entire agreement between 
              you and Trac Jobs Alert regarding the Service and supersede any prior agreements.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">19. Contact Information</p>
            <p className="text-slate-400 text-xs">
              For questions about these Terms, please contact us through our website contact form 
              at tracjobsalert.co.uk/contact
            </p>
          </section>

          <section className="space-y-1 bg-slate-800 p-3 rounded border border-emerald-500/30">
            <p className="text-slate-300 text-center">
              By using Trac Jobs Alert, you acknowledge that you have read, understood, and agree 
              to be bound by these Terms of Service and our Privacy Policy.
            </p>
          </section>

          <p className="text-slate-500 text-xs text-center pt-4 border-t border-slate-700 mt-4">
            Last updated: February 2026
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 px-4 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm">© 2026 tracjobsalert.co.uk - All rights reserved</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
